import React from "react";
import { useSelector, useDispatch } from "types/store";
import styles from "./style.module.css";
import Select from "react-select";
import numeral from "numeral";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { getRange } from "aible";
import { humanize } from "utils/misc";
import * as chatGPTReducer from "containers/ChatGPT/reducer";
import * as nextReducer from "containers/Next/NextReducer";
import { selectOnChangeWrapper } from "utils/select";
import { chainComparators, compareNumeric, compareString, getComparator } from "utils/collect";
const CustomTooltip = ({ active, payload, fKey }) => {
    if (active && payload && payload.length) {
        return (React.createElement("div", { className: styles.customTooltip },
            fKey !== undefined && React.createElement("p", { className: styles.customLabelBold }, `${fKey} is ${payload?.[0]?.payload[fKey]}`),
            payload?.[0]?.payload &&
                Object.keys(payload?.[0]?.payload)
                    .filter((pKey) => isNaN(payload?.[0]?.payload[pKey]) && pKey !== "name" && pKey !== fKey)
                    .map((pKey, pIndex) => React.createElement("p", { key: "ttip_" + pIndex }, `${pKey} is ${payload?.[0]?.payload[pKey]}`)),
            payload?.[0]?.name !== fKey && React.createElement("p", null, payload?.[0]?.name + " : " + payload[0].value),
            payload?.[1]?.name !== undefined && payload?.[1]?.name !== fKey && (React.createElement("p", null, payload?.[1]?.name + " : " + payload[1].value))));
    }
    return React.createElement(React.Fragment, null);
};
const SqlBarChart = ({ message, data, isEditChart, listItem }) => {
    const dispatch = useDispatch();
    const highlightIndices = listItem?.values ?? [];
    if (data?.[0] === undefined) {
        return React.createElement("div", null);
    }
    const isNumber = (x) => {
        return typeof x == "number" && !isNaN(x);
    };
    const numFields = Object.keys(data?.[0]).filter((f) => isNumber(data?.[0][f]) && Object.keys(data?.[0]).find((fKey) => "not_" + fKey === f) === undefined);
    const notFields = Object.keys(data?.[0])
        .filter((f) => isNumber(data?.[0][f]) && Object.keys(data?.[0]).find((fKey) => "not_" + fKey === f))
        .map((f) => ({
        field: f,
        parentField: Object.keys(data?.[0]).find((fKey) => "not_" + fKey === f)
    }));
    const textFields = Object.keys(data?.[0]).filter((f) => !isNumber(data?.[0][f]));
    const fKey = message?.chartXLabel ?? textFields?.[0];
    const retData = [...data].map((d) => {
        return fKey === undefined ? { ...d, name: Object.keys(d)?.[0] } : { ...d };
    });
    const updateMessage = (message) => {
        console.log("sql bar chart update message", message, message?.index);
        if (message?.index !== undefined) {
            dispatch(chatGPTReducer.updateMessage(message?.index, message));
        }
    };
    const nextId = useSelector((state) => nextReducer.getCurrentNextId(state));
    const activeTab = useSelector((state) => nextReducer.getActiveTab(state, nextId));
    const xAxisOptions = Object.keys(data?.[0])
        .filter((f) => f !== "name")
        .map((t) => ({ label: t, value: t }));
    const oKey = textFields.filter((t) => t !== fKey)?.[0];
    const compareJsonValue = (a, b) => {
        if (typeof a === "string" && typeof b === "string") {
            return compareString(a, b);
        }
        else if (typeof a === "number" && typeof b === "number") {
            return compareNumeric(a, b);
        }
        return 0;
    };
    const sortData = retData
        .slice()
        .sort(chainComparators([
        getComparator((x) => x[fKey], compareJsonValue),
        getComparator((x) => x[oKey], compareJsonValue)
    ]));
    return (React.createElement("div", { className: styles.sqlBarChartContainer },
        textFields.length > 1 && (activeTab === "CHATGPT" || isEditChart === true) && (React.createElement("div", { className: styles.sqlChartXAxis },
            React.createElement(Select, { options: xAxisOptions, onChange: selectOnChangeWrapper((val) => updateMessage({ ...message, chartXLabel: val })), value: message?.chartXLabel !== undefined
                    ? xAxisOptions.find((t) => t.value === message?.chartXLabel)
                    : xAxisOptions.find((t) => t.value === fKey) }))),
        message?.chart_type === "Number" || (message?.chart_type === undefined && !(sortData?.length > 1)) ? (React.createElement("div", { className: styles.numberChart, style: message?.chart_title === undefined ? {} : { marginTop: "30px" } }, sortData.map((d, i) => (React.createElement("div", { className: styles.numberChartRow, key: "sort_data_" + i }, Object.keys(d)
            .filter((cKey) => cKey !== "name")
            .map((cKey, cIndex) => (React.createElement("div", { className: styles.numberChartItem, key: "sort_data_" + i + "_" + cIndex },
            React.createElement("div", { className: styles.numberChartDesc }, cKey),
            React.createElement("div", { className: styles.numberChartVal }, !isNumber(d[cKey]) ? d[cKey] : humanize(d[cKey])))))))))) : message?.chart_type === "Bar" || message?.chart_type === undefined ? (numFields
            .filter((row) => row !== fKey || numFields.length === 1)
            .map((row, rIndex) => {
            let yMin = Infinity;
            let yMax = -Infinity;
            sortData.forEach((d) => {
                const val = d[row];
                if (typeof val == "number") {
                    yMin = Math.min(yMin, val);
                    yMax = Math.max(yMax, val);
                }
            });
            const yDomain = getRange(yMin, yMax, true, 1);
            // const retDomain = [yDomain[0] > 0 ? "auto" : yDomain[0], yDomain[1] < 0 ? "auto" : yDomain[1]];
            const retDomain = yDomain;
            const roundValues = Math.abs(yMax - yMin) > 10;
            return (React.createElement("div", { className: styles.sqlOuter, key: "fields_" + rIndex },
                React.createElement(ResponsiveContainer, { width: "100%", height: "100%" },
                    React.createElement(BarChart, { width: 500, height: 300, data: sortData, margin: {
                            top: message?.chart_title !== undefined ? 30 : 5,
                            right: 30,
                            left: 20,
                            bottom: 5
                        } },
                        React.createElement(CartesianGrid, { strokeDasharray: "3 3" }),
                        React.createElement(XAxis, { dataKey: fKey ?? "name" }),
                        React.createElement(YAxis, { domain: retDomain, tickFormatter: (tick) => {
                                const retTick = roundValues ? Math.round(tick) : tick;
                                return numeral(retTick).format("0.[00]a");
                            } }),
                        React.createElement(Tooltip, { content: React.createElement(CustomTooltip, { fKey: fKey }) }),
                        React.createElement(Bar, { dataKey: row, fill: "#8884d8" }, sortData.map((entry, index) => (React.createElement(Cell, { key: `cell-${index}`, fill: highlightIndices.find((hi) => data?.[hi?.[0]]?.[fKey] === entry[fKey]) !== undefined
                                ? "#F47C20"
                                : message?.chartColors
                                    ? message.chartColors[index]
                                    : nextReducer.colors[index % nextReducer.colors.length] })))),
                        notFields &&
                            notFields.find((n) => n.parentField === row) !== undefined &&
                            notFields.map((not, i) => (React.createElement(Bar, { dataKey: not.field, fill: "rgba(180, 180, 180)", key: "notfields_" + i }, sortData.map((entry, index) => (React.createElement(Cell, { key: `cell-${index}`, fill: highlightIndices.find((hi) => hi?.[0] === index && hi?.[1] === not.field) !== undefined
                                    ? "#F47C20"
                                    : "rgba(180, 180, 180)" }))))))))));
        })) : message?.chart_type === "Line" ? (numFields
            .filter((row) => row !== fKey || numFields.length === 1)
            .map((row, nIndex) => (React.createElement("div", { className: styles.sqlOuter, key: "notfields_" + nIndex },
            React.createElement("div", { className: styles.sqlChartTitle }, fKey + " by " + row),
            React.createElement(ResponsiveContainer, { width: "100%", height: "100%" },
                React.createElement(LineChart, { width: 500, height: 300, data: sortData, margin: {
                        top: message?.chart_title !== undefined ? 30 : 5,
                        right: 30,
                        left: 20,
                        bottom: 5
                    } },
                    React.createElement(CartesianGrid, { strokeDasharray: "3 3" }),
                    React.createElement(XAxis, { dataKey: fKey ?? name }),
                    React.createElement(YAxis, null),
                    React.createElement(Tooltip, { content: React.createElement(CustomTooltip, null) }),
                    React.createElement(Line, { dataKey: row, fill: "#8884d8" }, sortData.map((entry, index) => (React.createElement(Cell, { key: `cell-${index}`, fill: nextReducer.colors[index % nextReducer.colors.length] })))),
                    notFields &&
                        notFields.find((n) => n.parentField === row) !== undefined &&
                        notFields.map((not, i) => (React.createElement(Line, { dataKey: not.field, fill: "rgba(180, 180, 180)", key: "notfields_" + nIndex + "_" + i }, sortData.map((entry, index) => (React.createElement(Cell, { key: `cell-${index}`, fill: "rgba(180, 180, 180)" })))))))))))) : message?.chart_type === "Pie" ? (numFields.map((tRow, nIndex) => (React.createElement(ResponsiveContainer, { width: 100 / numFields.length + "%", height: "100%", key: "numfields_" + nIndex },
            React.createElement(PieChart, { width: 100, height: 100, margin: {
                    top: message?.chart_title !== undefined ? 30 : 5,
                    right: 30,
                    left: 20,
                    bottom: 5
                } },
                React.createElement(Tooltip, null),
                React.createElement(Legend, null),
                React.createElement(Pie, { data: retData, dataKey: tRow, fill: "#8884d8" }, retData.map((entry, index) => (React.createElement(Cell, { key: `cell-${index}`, fill: nextReducer.colors[index % nextReducer.colors.length] }))))))))) : (React.createElement("div", null))));
};
export default SqlBarChart;
